import MainLayout from 'components/layout/MainLayout';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { capitalizeFirstLetter, singularOrPlural } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';

import { TTicketDetailsOnlyLotteryDrawEntry } from '@daymade/api-contracts';
import { BUSINESS_TIME_ZONE } from '@daymade/common-lib/constants';
import { TLotteryDrawEntry } from '@daymade/common-lib/dbJsonModels';
import { longStringFromDate } from '@daymade/common-lib/formatting';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import MyAccountLink from 'components/account/MyAccountLink';
import TicketStack from 'components/dashboard/entries-and-stats/TicketStack';
import DrawCounter from 'components/home-page/DrawCounter';
import { randomPhrases } from 'components/landing/StrongerVariant/PrizesSection/WelcomeBackSection';
import PageTitle from 'components/PageTitle';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import RoundedButton from 'components/RoundedButton';
import { useAuthRedirectEffect } from 'hooks/account/useAuthRedirect';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { TSubscriptions, UserStore } from 'stores/UserStore';
import { ENTER_CTA_LINK } from 'utils/cta/CTA';
import History from '../../components/svg/History.svg';
import styles from './index.module.css';

const Index = observer(
  ({
    redeemTokens,
  }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const userStore = useUserStore();
    const {
      currentUser,
      updateCurrentUser,
      logout,
      activeSubscriptions,
      isOperator,
      claimablePrizesCount,
      subscriptionStatusInfo,
    } = userStore;
    const treesPlanted = currentUser?.treesPlanted;

    const [loading, setLoading] = useState(true);

    const [latestEntriesExpanded, setLatestEntriesExpanded] = useState(true);
    const [myImpactExpanded, setMyImpactExpanded] = useState(false);

    const [randomPhrase, setRandomPhrase] = useState<string | null>(null);
    useAuthRedirectEffect('/account');

    useEffect(() => {
      (async () => {
        setRandomPhrase(
          randomPhrases[Math.floor(Math.random() * randomPhrases.length)],
        );
        setLoading(false);

        updateCurrentUser();
      })();
    }, []);

    // Forward to Tokens page if visiting with appropriate query parameter.
    useEffect(() => {
      (async () => {
        if (redeemTokens) {
          Router.push('/token-town');
        }
      })();
    }, [redeemTokens]);

    if (loading || !currentUser || !activeSubscriptions) {
      return (
        <MainLayout additionalClassName="accountPage">
          <NextSeo
            title="Account | Win Epic Prizes | DAYMADE"
            openGraph={{
              title: 'Account | Win Epic Prizes | DAYMADE',
            }}
          />

          <PageTitle
            heroBackgroundImage={`${ASSET_PREFIX}/images/account/page-title-background.svg`}
          >
            My <strong>account</strong>
          </PageTitle>

          <PlainLoadingSpinner style={{ margin: '10em' }} />
        </MainLayout>
      );
    }
    const activeSubscription = activeSubscriptions[0];

    return (
      <MainLayout additionalClassName="accountPage" limitItemWidths>
        <NextSeo
          title="Account | Win Epic Prizes | DAYMADE"
          openGraph={{
            title: 'Account | Win Epic Prizes | DAYMADE',
          }}
        />
        <div className="daymadeTypographyReset">
          <PageTitle
            rebrandDm2025
            heroBackgroundImage={`${ASSET_PREFIX}/images/account/page-title-background.svg`}
          >
            Account
          </PageTitle>

          {loading || !currentUser || !activeSubscriptions ? (
            <PlainLoadingSpinner style={{ margin: '150px auto' }} />
          ) : (
            <>
              <AboutUsModule background="white">
                <AboutUsSiteContainer>
                  <div className={styles.sideBySide}>
                    <div className={styles.sideBySideItem}>
                      <h2
                        className={
                          styles.heading + ' aboutUsTypographyHeaderLarge'
                        }
                      >
                        My Entries
                      </h2>
                      <div>
                        <div className={styles.myAccountBox}>
                          <MyAccountEntriesContent
                            activeSubscription={activeSubscription}
                            subscriptionStatusInfo={subscriptionStatusInfo}
                          />
                        </div>
                        {!activeSubscription && (
                          <div className={styles.rogueLink}>
                            <MyAccountLink
                              href="/account/entries"
                              imageElement={<History />}
                            >
                              Entry History
                            </MyAccountLink>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.sideBySideItem}>
                      <h2
                        className={
                          styles.heading + ' aboutUsTypographyHeaderLarge'
                        }
                      >
                        My Benefits
                      </h2>
                      <div
                        className={
                          styles.myAccountBox +
                          ' ' +
                          styles.myAccountBoxCentered
                        }
                      >
                        <ul className={styles.benefitsList}>
                          <li>
                            <img
                              src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/TealMyImpact.png"
                              alt=""
                            />
                            <span>
                              Plant a tree every week with{' '}
                              <a href="https://www.veritree.com/">veritree</a>
                            </span>
                          </li>
                          <li>
                            <img
                              src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/TealDiscounts.png"
                              alt=""
                            />
                            <span>
                              Discounts with our{' '}
                              <Link href="/my-discounts">brand partners</Link>
                            </span>
                          </li>
                          <li>
                            <img
                              src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/TealLeaderboard.png"
                              alt=""
                            />
                            <span>
                              Automatic entry into our monthly{' '}
                              <Link href="/leaderboard">
                                Leaderboard Raffle
                              </Link>
                            </span>
                          </li>
                          <li>
                            <img
                              src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/TealTokenTown.png"
                              alt=""
                            />
                            <span>
                              Access to{' '}
                              <Link href="/token-town">Token Town</Link>
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className={styles.sideBySideItem}>
                      <h2
                        className={
                          styles.heading + ' aboutUsTypographyHeaderLarge'
                        }
                      >
                        My Subscription
                      </h2>
                      <ul className={styles.linkBox}>
                        <li>
                          <MyAccountLink
                            imageElement={
                              <img
                                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/DrawResults.png"
                                alt=""
                              />
                            }
                            href="/results"
                          >
                            Draw Results
                          </MyAccountLink>
                        </li>
                        <li>
                          <MyAccountLink
                            imageElement={
                              <img
                                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/MyPrizes.png"
                                alt=""
                              />
                            }
                            href="/my-wins"
                            badgeText={
                              claimablePrizesCount > 0
                                ? `Unclaimed ${singularOrPlural(claimablePrizesCount, 'Win', 'Wins')}`
                                : undefined
                            }
                          >
                            My Wins
                          </MyAccountLink>
                        </li>
                        {activeSubscriptions.length > 0 && (
                          <li>
                            <MyAccountLink
                              imageElement={
                                <img
                                  src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/MySubscription.png"
                                  alt=""
                                />
                              }
                              href="/account/subscriptions"
                            >
                              Manage Subscription
                            </MyAccountLink>
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className={styles.sideBySideItem}>
                      <h2
                        className={
                          styles.heading + ' aboutUsTypographyHeaderLarge'
                        }
                      >
                        My Profile
                      </h2>
                      <ul className={styles.linkBox}>
                        {isOperator && (
                          <li>
                            <MyAccountLink
                              imageElement={
                                <img
                                  src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/ChangePassword.png"
                                  alt=""
                                />
                              }
                              href="/admin"
                            >
                              Admin Console
                            </MyAccountLink>
                          </li>
                        )}
                        <li>
                          <MyAccountLink
                            imageElement={
                              <img
                                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/MyDetails.png"
                                alt=""
                              />
                            }
                            href="/account/change-details"
                          >
                            My Details
                          </MyAccountLink>
                        </li>
                        <li>
                          <MyAccountLink
                            imageElement={
                              <img
                                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/EmailPreferences.png"
                                alt=""
                              />
                            }
                            href="/account/email-preferences"
                          >
                            Email Preferences
                          </MyAccountLink>
                        </li>
                        <li>
                          <MyAccountLink
                            imageElement={
                              <img
                                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/ChangePassword.png"
                                alt=""
                              />
                            }
                            href="/account/change-password"
                          >
                            Change Password
                          </MyAccountLink>
                        </li>
                        <li>
                          <MyAccountLink
                            imageElement={
                              <img
                                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/SignOut.png"
                                alt=""
                              />
                            }
                            onClick={logout}
                          >
                            Sign Out
                          </MyAccountLink>
                        </li>
                      </ul>
                    </div>
                  </div>
                </AboutUsSiteContainer>
              </AboutUsModule>

              <AboutUsModule background="tealGradient">
                <AboutUsSiteContainer>
                  <div className={styles.helpContainer}>
                    <h2 className="aboutUsTypographyHeaderXlargeOnTeal">
                      Need help?
                    </h2>
                    <p>
                      Our support page will help answer your most frequent
                      questions! Have a look{' '}
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href="https://daymade.zendesk.com/hc/en-gb"
                      >
                        here
                      </a>
                      .
                    </p>
                  </div>
                </AboutUsSiteContainer>
              </AboutUsModule>
            </>
          )}
        </div>
      </MainLayout>
    );
  },
);

const MyAccountEntriesContent = (props: {
  activeSubscription: TSubscriptions[number];
  subscriptionStatusInfo: UserStore['subscriptionStatusInfo'];
}) => {
  const userStore = useUserStore();
  const drawStore = useDrawStore();
  const [entriesExpanded, setEntriesExpanded] = useState(false);
  if (!props.activeSubscription) {
    return (
      <>
        <div className={styles.noSubscription}>
          This account is not currently subscribed to any plan.
        </div>
        {drawStore.upcomingDrawDate &&
          renderCountdown(drawStore.upcomingDrawDate, undefined)}
        {!props.activeSubscription && (
          <div className={styles.buttonContainer}>
            <RoundedButton href={ENTER_CTA_LINK} rebrandDm2025>
              Enter now
            </RoundedButton>
          </div>
        )}
      </>
    );
  }

  const drawDateToShow =
    userStore.upcomingSubscriptionDrawDate || drawStore.upcomingDrawDate;
  // Only use the first ticket, which is what plasmic did, probably because the Tuesday and Friday draws will have two
  // tickets purchased
  let ticket = userStore.activeWeeklyDrawSubscriptionTickets?.[0] as
    | Pick<TTicketDetailsOnlyLotteryDrawEntry, 'entries' | 'drawTypePlan'>
    | undefined;
  let usingDrawOrderEntries = false;

  if (!ticket && props.activeSubscription.subscription.status === 'ACTIVE') {
    // The ticket may not have been purchased yet, use the numbers attached to the drawOrder of the subscription if the
    // subscription is active
    ticket = {
      entries: props.activeSubscription.drawOrder
        .entries as TLotteryDrawEntry[],
      drawTypePlan: props.activeSubscription.drawSchedulePlans[0].drawTypePlan,
    };
    usingDrawOrderEntries = true;
  }

  // It is possible for someone's next draw date to not be the next appropriate draw for their subscription if the
  // player has snoozed or subscribed at certain times. Do not show the draw date countdown for this rare scenario
  const inconsistentNextDrawDate =
    usingDrawOrderEntries &&
    drawDateToShow &&
    props.activeSubscription.subscription.nextPaymentDate > drawDateToShow;

  return (
    <>
      <div className={styles.subscriptionHeader}>
        <h3
          className={'aboutUsTypographyHeaderMedium ' + styles.subscriptionType}
        >
          {capitalizeFirstLetter(
            props.subscriptionStatusInfo.subscriberStatus || '',
          )}{' '}
          Plan
        </h3>
        <div
          className={
            'aboutUsTypographyCaption ' + styles.subscriptionStatusBadge
          }
        >
          {props.activeSubscription.subscription.status}
        </div>
      </div>
      {!ticket && (
        <>
          <p>
            Your subscription for the upcoming draw has not been renewed yet.
            You can review your previous entries below.
          </p>
          <p>
            <Link href="/account/entries" className="textDecorationUnderline">
              See previous entries
            </Link>
          </p>
        </>
      )}
      {ticket && (
        <>
          <div className={styles.entriesContainer}>
            <div className={styles.entriesContainerInner}>
              <TicketStack
                entries={ticket.entries}
                expanded={entriesExpanded}
                hideExpandButton
              />
            </div>
            {usingDrawOrderEntries && (
              <p className="aboutUsTypographyCentered">
                These entries will be purchased when your subscription renews on{' '}
                {longStringFromDate(
                  props.activeSubscription.subscription.nextPaymentDate,
                  { includeWeekdayName: false, timeZone: BUSINESS_TIME_ZONE },
                )}
              </p>
            )}
          </div>

          <div className={styles.entriesLinks}>
            {ticket && ticket.entries.length > 1 ? (
              <button onClick={() => setEntriesExpanded((x) => !x)}>
                {entriesExpanded ? 'Collapse' : 'Expand'}
              </button>
            ) : (
              <div></div>
            )}
            <Link href="/account/entries">
              Entry History
            </Link>
          </div>
        </>
      )}
      {drawDateToShow &&
        !inconsistentNextDrawDate &&
        renderCountdown(drawDateToShow, 'Your next draw is in')}
    </>
  );
};

function renderCountdown(date: Date, introText: string | undefined) {
  return (
    <div className={styles.countdownOuter}>
      <div className={styles.countdown}>
        <img
          src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/account/Clock.png"
          alt=""
        />
        <DrawCounter targetDate={date} bare introText={introText} />
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const redeemTokens = ctx.query.action === 'redeem-tokens';

  return {
    props: {
      redeemTokens,
    },
  };
};

export default Index;
